<template>
  <div id='un_productdesign_container' :class='environmentClass'>
    <router-link
      tag='p'
      v-if='isGoldEnv'
      :to='{name: "product-listing", params: { categoryId: $route.params.categoryId, itemId: $route.params.itemId }}'
      class='back-btn'>Back</router-link>

    <SpinningLoader class='spinning_loader' v-if='loading || rendering' />

    <div :class='{active: !loading}' id='un_productdesign_wrap'>
      <div class='acceptance-controls_wrap' :class='{deactivated: deactivated, uploading: uploading}'>
        <div class='acceptance-options'>
          <button class='cancel-btn' v-tap='handleCancelButton' :disabled='hideCancelButton'>
            {{ cancelButtonText }}
          </button>

          <span class="btn-separator"></span>

          <button v-tap='handleDoneButton' :disabled='!userImageExists'>
            {{ doneButtonText }}
          </button>
        </div>
      </div>

      <div id='un_user-alert-container' v-if='userAlerts.length'>
        <UserAlert
          v-for='alert in userAlerts'
          :alert='alert'
          :key='alert.id'
          @dismiss='dismissAlert'>
        </UserAlert>
      </div>

      <FileUploadProgress
        class='fileUploadEl'
        v-if='uploading'
        :uploadProgress='uploadProgress'
      />

      <ArtCanvas
        class='artCanvasEl'
        :canvas='canvas'
        :class='{deactivated: deactivated, uploading: uploading}'
        :currentImage='currentImage'
        :masks='visibleMasks'
        :userImageExists='userImageExists'
        @draggedIn='handleDraggedIn'
        @dragNotSupported='handleNotSupported'
        @fileChange='handleFilesChange'
        @fileSelectionTriggered='triggerInput'
        @hoverImage='handleImageHover'
        @hoverMask='handleMaskHover'
        @selectImg='handleSelectImg'
        @setSelectedMask='handleSetSelectedMask'
        @viaDialog='draggedInOrViaDialog = "viaDialog"'>
      </ArtCanvas>

      <DesignControls
        id='un_productdesign_controls'
        class='z_4 ta_c pos_a'
        :class='{deactivated: deactivated, uploading: uploading}'
        :activeControl='activeControl'
        :canvas='canvas'
        :currentImage='currentImage'
        :imageIsSelected='imageIsSelected'
        :userImageExists='userImageExists'
        @resetImage='resetImage'
        @imageControlChanged='handleImageControlChanged'
        @removeImage='handleRemoveImage'>
      </DesignControls>

      <div id='un_completedesigncontrol_wrap'></div>

      <div id='hidden_input_wrap' v-show='false'>
        <input type="file" id='art_image_upload' v-show='false' name="image"
          @change="handleFilesChange($event.target.name, $event.target.files, $event);"
          accept=".jpg, .jpeg, .png">
      </div>
    </div>

    <div v-if='noTemplate && !loading'>
      <p>There is no template available for this product.</p>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import uuid from 'uuid/v4'
import {axiosBase} from '$api/config'
import {f as fabric} from '$utilities/fabric-and-extensions'

import {
  ENDPOINT_VARIANTS,
  ENDPOINT_DECORATION_ASSET,
  BY_TEMPLATE_ID,
  BY_W,
  BY_H
} from '$api/endpoints'

import {downloadAsset, dataURLtoBlob} from '$utilities/ImgToB64'
import {makeid} from '$utilities/hash'
import {getScaleFactor} from '$utilities/imageSetting'
import {isThisMobileDevice} from '$utilities/device'

import {
  ALERT_USER_EVENT,
  RENDER_COMPLETE_EVENT,
  RENDER_START_EVENT,
  REVEAL_INTRO_EVENT,
  unUi
} from '@/components/EventBuses'

import ArtCanvas from './ArtCanvas.vue'
import DesignControls from './design-controls/DesignControls.vue'
import Check from '$svgs/Check.vue'
import SpinningLoader from '$shared/loaders/SpinningLoader.vue'
import FileUploadProgress from './FileUploadProgress.vue'
import UserAlert from './UserAlert.vue'
import {ENV, isGoldEnv, isShopifyEnv} from '$utilities/environment'
import {getExifData} from '$utilities/exif'

import {
  MSG_CONFIRM_USER_CANCEL_INTENT,
  MSG_CONFIRM_USER_DONE_INTENT_GOLD,
  MSG_CONFIRM_USER_DONE_INTENT_SHOPIFY,
  MSG_UPLOAD_FAILED,
  MSG_UPLOADING_START,
  MSG_UPLOAD_COMPLETE,
  MSG_ALERT_IMAGE_TOO_SMALL
} from '$language'

const ALERT_DISMISS_INTERVAL_SECONDS = 7
const CUSTOMIZATION_COMPLETE_EVENT = 'UNCOMMON_CUSTOMIZATION_COMPLETE'

const ASSET_DEBUG = false
const ASSET_OUTPUT_JPEG = 'jpeg'
const ASSET_OUTPUT_PNG = 'png'

const ASSET_OUTPUT_FORMATS_TO_FILE_EXTENSION = {
  [ASSET_OUTPUT_PNG]: 'png',
  [ASSET_OUTPUT_JPEG]: 'jpg'
}

const extensionForOutputFormat = (format) => {
  return ASSET_OUTPUT_FORMATS_TO_FILE_EXTENSION[format]
}

// This is the significant constant that changes the output of
// the assets that the customizer is producing
const ASSET_OUTPUT_FORMAT = ASSET_OUTPUT_PNG

// Fabric demands 'jpeg' for operations but we prefer the file extension 'jpg'
const ASSET_OUTPUT_EXTENSION = extensionForOutputFormat(ASSET_OUTPUT_FORMAT)

// XXX DEBUG
const genSecondsSinceEpoch = () => {
  return Math.floor(new Date().getTime() / 1000)
}

export default {
  props: [
    'deactivated'
  ],

  data () {
    return {
      activeControl: null,
      activeMask: null,
      activeSelection: null,
      artboard_height: null,
      artboard_width: null,
      canvas: null,
      drag: { over: false, supported: false },
      draggedInOrViaDialog: null,
      environmentClass: `env-${ENV}`,
      globalScaleFactor: null,
      imageFileList: [],
      imageIsSelected: false,
      initialUserImageScale: null,
      inputEl: null,
      loading: true,
      masks: [],
      rendering: false,
      selectedMask: null,
      selectedTemplateName: null,
      template: null,
      uploading: false,
      uploadProgress: {
        message: MSG_UPLOADING_START,
        progress: 0
      },
      userAlerts: [],
      visibleMasks: []
    }
  },

  computed: {
    isGoldEnv,
    isShopifyEnv,

    cancelButtonText () {
      return (this.isGoldEnv) ? 'Reset' : 'Cancel'
    },

    doneButtonText () {
      return (this.isGoldEnv) ? 'Finish & Download' : 'Done'
    },

    hideCancelButton () {
      return (this.isGoldEnv) && (this.userImageExists === false)
    },

    activated () {
      return this.deactivated === false
    },

    currentImage () {
      return this.imageFileList.find(image => {
        return image.id === this.canvas.getActiveObject().id
      })
    },

    userImageExists () {
      return this.imageFileList.length > 0
    },

    isMobile () {
      return isThisMobileDevice()
    },

    noTemplate () {
      return (this.template === null)
    },

    templates () {
      if (!this.canvas) { return null }

      return {
        edgeToEdge: [
          {
            id: 'ete1',
            x: 0,
            y: 0,
            width: this.canvas.width,
            height: this.canvas.height,
            img: null,
            activeSelection: false,
            type: 'Rect',
            maskObj: {
              id: 'ete1',
              width: this.canvas.width,
              height: this.canvas.height,
              top: 0,
              left: 0,
              absolutePositioned: true
            }
          }
        ],

        centerSplit: [
          {
            id: 'ete1',
            x: 0,
            y: 0,
            width: this.canvas.width / 2,
            height: this.canvas.height,
            img: null,
            activeSelection: false,
            type: 'Rect',
            maskObj: {
              id: 'ete1',
              width: this.canvas.width / 2,
              height: this.canvas.height,
              top: 0,
              left: 0,
              absolutePositioned: true
            }
          },
          {
            id: 'cs2',
            x: this.canvas.width / 2,
            y: 0,
            width: this.canvas.width / 2,
            height: this.canvas.height,
            img: null,
            activeSelection: false,
            type: 'Rect',
            maskObj: {
              id: 'cs2',
              width: this.canvas.width / 2,
              height: this.canvas.height,
              top: 0,
              left: this.canvas.width / 2,
              absolutePositioned: true
            }
          }
        ]
      }
    },

    selectedTemplate () {
      return this.templates[this.selectedTemplateName]
    }
  },

  watch: {
    imageIsSelected: function (value) {
      if (value) { this.resetUserAlerts() }
    },

    template: function (newTemplate, oldTemplate) {
      if (!newTemplate) { return }
      if (newTemplate === oldTemplate) { return }

      this.initFabric()
    }
  },

  methods: {
    activateCustomizer () {
      this.$emit('activate-customizer')
    },

    closeCustomizer () {
      this.$emit('deactivate-customizer')
    },

    triggerInput () {
      document.getElementById('art_image_upload').click()
    },

    rebuildInput () {
      if (document.getElementById('art_image_upload')) {
        document.getElementById('art_image_upload').remove()
      }

      this.inputEl = document.createElement('input')
      let wrapper = document.getElementById('hidden_input_wrap')

      this.inputEl.setAttribute('id', 'art_image_upload')
      this.inputEl.setAttribute('type', 'file')
      this.inputEl.setAttribute('name', 'image')
      this.inputEl.setAttribute('accept', '.jpg, .jpeg, .png')

      this.inputEl.addEventListener('change', ($event) => {
        this.handleFilesChange($event.target.name, $event.target.files, $event)
      })
      wrapper.appendChild(this.inputEl)
    },

    handleNotSupported () {
      console.log('canvas drag and drop not supported!')
    },

    resetUserAlerts () {
      this.userAlerts = []
    },

    alertUser ({ message, type }) {
      let alert = {
        message,
        id: uuid(),
        type: type || 'warning'
      }

      this.userAlerts.unshift(alert)

      // Automatically dismiss alerts after an interval.
      setTimeout(() => {
        this.dismissAlert(alert.id)
      }, ALERT_DISMISS_INTERVAL_SECONDS * 1000)
    },

    dismissAlert (alertId) {
      this.userAlerts = _.reject(this.userAlerts, (alert) => {
        return alert.id === alertId
      })
    },

    resetImage () {
      this.setUserImage()
      unUi.$emit('resetImage')
    },

    handleImageControlChanged (controlName) {
      this.activeControl = controlName
    },

    handleFilesChange: function (fieldName, fileList) {
      var vm = this
      this.loading = true

      if (!fileList.length) {
        return
      }

      let imageFile = fileList[0]
      let imageUrl = URL.createObjectURL(imageFile)

      getExifData(imageFile).then((exifData) => {
        vm.setUserImage({
          imageUrl,
          exifData
        })
      })
    },

    handleMaskHover (mask) {
      this.activeMask = mask.id
      // HANDLE HOVER ACTIONS / UPDATES
      this.canvas.renderAll()
    },

    handleImageHover (mask) {
      let hoveredImg = this.selectAnyObjectById(mask.img)
      this.canvas.bringToFront(hoveredImg)
      this.canvas.renderAll()
    },

    handleSetSelectedMask (mask) {
      if (!mask) { return }

      this.selectedMask = mask.id

      // TODO MASKS?
      // Not sure what this is doing. Debug? Sets a stroke with a width of 0.
      //
      // let selectedMask = this.selectAnyObjectById(mask.id)
      // selectedMask.set({
      //   stroke: 'rgba(0, 0, 0, 0)',
      //   strokeWidth: 0
      // })
      // this.canvas.renderAll()
    },

    handleDraggedIn (mask) {
      this.draggedInOrViaDialog = 'draggedIn'
      this.activeMask = mask.id
    },

    handleSelectImg (mask) {
      this.deselectAllMasks()

      if (mask) {
        let selectedImg = this.selectAnyObjectById(mask.img)
        this.canvas.bringToFront(selectedImg)
      }
    },

    deselectAllMasks () {
      // TODO MASKS?

      // Related to handleSelectedMask
      // let allObjs = this.canvas.getObjects()

      // allObjs.forEach(obj => {
      //   const selectedState = {
      //     stroke: 'rgba(0, 0, 0, 0)',
      //     strokeWidth: 0
      //   }
      //   obj.set(selectedState)
      // })

      // this.canvas.renderAll()
    },

    selectAnyObjectById (id) {
      let allObjs = this.canvas.getObjects()
      let foundObject = allObjs.filter(obj => {
        return obj.id === id
      })
      return foundObject[0]
    },

    handleCancelButton (event) {
      // If the user has an image set, we first ask if they are sure they want to abandon.
      if (this.userImageExists && (!confirm(MSG_CONFIRM_USER_CANCEL_INTENT))) {
        return
      }

      this.handleRemoveImageAndTearDownCustomizer()
    },

    handleDoneButton (event) {
      if (!this.userImageExists) { return }

      let confirmationMessage = (this.isShopifyEnv)
        ? MSG_CONFIRM_USER_DONE_INTENT_SHOPIFY
        : MSG_CONFIRM_USER_DONE_INTENT_GOLD

      if (confirm(confirmationMessage)) {
        this.completeDesign()
      }
    },

    completeDesign () {
      // Render exactly the user sees, so we can put this in their cart
      var cartImage = this.canvas.toDataURL({
        format: 'jpeg',
        quality: 1,
        multiplier: 0.5
      })

      if (ASSET_DEBUG === true) {
        let timestamp = genSecondsSinceEpoch()
        downloadAsset(cartImage, `${timestamp}_cart_image.jpg`)
      }

      this.uploading = true

      this.uploadProgress.message = MSG_UPLOADING_START
      this.uploadProgress.progress = 0

      // Strip the background and overlay image layers.
      // We use a timeout here so that the canvas element has time to
      // animate into oblivion before we start ripping away layers.
      this.$nextTick(function () {
        this.canvas.backgroundImage = null
        this.canvas.overlayImage = null
      })

      this.canvas.renderAll()

      // Now we have our pure asset, devoid of any decoration
      let vm = this

      // Here we're waiting half a second to ensure that the canvas
      // has finished rendering. There is likely a better way to do this.
      setTimeout(() => {
        // Grab this as a PNG to prevent any loss in quality.
        // It is converted to a JPG in renderFullQuality
        const multiplier = 4

        const inputImage = this.canvas.toDataURL({
          format: 'png',
          multiplier: multiplier
        })

        vm.renderAsFullQuality({
          inputImage,
          sourceWidth: Math.floor(this.canvas.width * multiplier),
          sourceHeight: Math.floor(this.canvas.height * multiplier)
        }).then((assetImage) => {
          vm.handleCompletedDesign({
            assetImage,
            cartImage
          })
        })
      }, 500)
    },

    handleCompletedDesign ({ assetImage, cartImage }) {
      let payload = {
        assetUUID: uuid(),
        fullQualityImage: assetImage,
        cartImage
      }

      let handler = (this.isShopifyEnv)
        ? this.uploadCompletedDesignToS3
        : this.downloadCompletedDesign

      handler(payload)
    },

    renderAsFullQuality ({ inputImage, sourceWidth, sourceHeight }) {
      const vm = this

      return new Promise((resolve, reject) => {
        var renderer = document.getElementById('art_renderer')

        if (!renderer) {
          renderer = document.createElement('canvas')
          renderer.id = 'art_renderer'

          renderer.width = vm.template.production_size.w
          renderer.height = vm.template.production_size.h

          document.body.appendChild(renderer)
        }

        var ctx = renderer.getContext('2d')

        ctx.imageSmoothingEnabled = false
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)

        var outputImage = new Image()

        outputImage.onerror = () => {
          reject(new Error('An unknown error occurred while trying to render the full quality image.'))
        }

        outputImage.onload = () => {
          let sourceX = 2
          let sourceY = 2
          let destinationX = 0
          let destinationY = 0
          let destinationWidth = vm.template.production_size.w
          let destinationHeight = vm.template.production_size.h

          // Here we use the most elaborate form of drawImage to clip out artifacts
          // and ensure our output image is in the specific dimensions we want.
          // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
          ctx.drawImage(
            outputImage,
            // Source Attributes
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            // Destination Attributes
            destinationX,
            destinationY,
            destinationWidth,
            destinationHeight
          )

          const mimetype = `image/${ASSET_OUTPUT_FORMAT}`
          const quality = 1.0

          var fullQualityImage = renderer.toDataURL(mimetype, quality)

          resolve(fullQualityImage)
        }

        outputImage.src = inputImage
      })
    },

    updateProgressPercentage (progressMap) {
      const items = _.values(progressMap)

      if (!items.length) { return }

      const total = _.reduce(items, (sum, {total}) => { return total + sum }, 0)
      const progress = _.reduce(items, (sum, {loaded}) => { return loaded + sum }, 0)

      this.uploadProgress.progress = (progress / total) * 100
    },

    uploadCompletedDesignToS3 ({ assetUUID, fullQualityImage, cartImage }) {
      // In the Shopify environment, we're going to upload these to S3
      // and then emit a global event with the path information so that
      // the Shopify system can take the reigns and add the item to the cart.
      const vm = this

      // We keep track of progress by key.
      // Each value in this map will contain a map of { loaded, total }
      // which represent the respective bytes uploaded vs. total to upload
      const progress = {}

      // Amplify will hit this callback w/ progress.
      // We update the progress map and then hand it off to
      // a calculator method which will determine the percentage
      // that we're complete and set that in the component state.
      const updateProgressFromCallback = ({ key, loaded, total }) => {
        progress[key] = { loaded, total }
        vm.updateProgressPercentage(progress)
      }

      const uploadOptions = {
        level: 'public',
        contentType: 'image/jpeg',
        progressCallback: updateProgressFromCallback
      }

      const uploadCartImage = this.$Amplify.Storage.put(
        `${assetUUID}_cart.jpg`,
        dataURLtoBlob(cartImage),
        uploadOptions
      )

      const uploadAssetImage = vm.$Amplify.Storage.put(
        `${assetUUID}.${ASSET_OUTPUT_EXTENSION}`,
        dataURLtoBlob(fullQualityImage),
        {
          ...uploadOptions,
          contentType: `image/${ASSET_OUTPUT_FORMAT}`
        }
      )

      // Uncomment to force all uploads to fail after 2 seconds.
      // Used during the build/debugging of error handling.
      //
      // return setTimeout(() => {
      //   this.handleS3UploadFailure()
      // }, 2000)

      Promise.all([uploadCartImage, uploadAssetImage])
        .catch(this.handleS3UploadFailure)
        .then(this.handleS3UploadSuccess)
    },

    handleS3UploadFailure (_error) {
      // Show an alert to the user
      this.alertUser({
        message: MSG_UPLOAD_FAILED
      })

      // Bring the template decoration back and re-render canvas.
      this.setTemplateImages(() => {
        this.uploading = false
      })
    },

    handleS3UploadSuccess ([cartImageResult, assetImageResult]) {
      this.uploadProgress.message = MSG_UPLOAD_COMPLETE
      this.uploadProgress.progress = 100

      const config = this.$Amplify.Storage._config.AWSS3
      const bucketPrefix = `https://${config.bucket}.s3.amazonaws.com/public`

      const eventPayload = {
        cart: `${bucketPrefix}/${cartImageResult.key}`,
        asset: `${bucketPrefix}/${assetImageResult.key}`
      }

      this.onCustomizationUploadComplete(eventPayload)
    },

    downloadCompletedDesign ({ assetUUID, fullQualityImage }) {
      // In the Gold environment, we're simply going to shoot the
      // full-quality image to the browser via direct download.
      downloadAsset(fullQualityImage, `${assetUUID}.${ASSET_OUTPUT_EXTENSION}`)

      this.uploadProgress.progress = 100

      this.setTemplateImages(() => {
        this.uploading = false
      })

      this.handleRemoveImage()
    },

    onCustomizationUploadComplete (assetData) {
      let event = new CustomEvent(CUSTOMIZATION_COMPLETE_EVENT, {
        detail: assetData
      })

      document.dispatchEvent(event)

      setTimeout(this.closeCustomizer, 500)
    },

    bindImageEvents (img) {
      img.on('selected', () => {
        this.imageIsSelected = true
      })

      img.on('deselected', () => {
        this.imageIsSelected = false
      })
    },

    handleRemoveImage () {
      // TODO ensure that we're doing proper cleanup here
      // so that the browser can GC leftover canvas objects/contexts.
      let activeObject = this.canvas.getActiveObject()

      if (!activeObject) { return }

      this.visibleMasks.forEach((visibleMask, i) => {
        if (visibleMask.img === activeObject.id) {
          visibleMask.img = null
        }
      })

      this.imageFileList.forEach((image, i) => {
        if (image.id === activeObject.id) {
          this.imageFileList.splice(i, 1)
        }
      })

      this.activeControl = null
      this.canvas.remove(activeObject)

      activeObject.dispose()

      this.deselectAllMasks()

      this.rebuildInput()
    },

    handleRemoveImageAndTearDownCustomizer () {
      this.handleRemoveImage()

      if (this.isShopifyEnv) {
        this.closeCustomizer()
      }
    },

    setUserImage ({ imageUrl, exifData }) {
      var vm = this

      const heightThreshold = vm.template.production_size.h * 0.5
      const widthThreshold = vm.template.production_size.w * 0.5

      const handleImageCallback = (fabricImage) => {
        let imageTooSmall = fabricImage.height < heightThreshold || fabricImage.width < widthThreshold

        if (imageTooSmall) {
          vm.alertUser({
            message: MSG_ALERT_IMAGE_TOO_SMALL(vm.template)
          })
        } else {
          // TODO this will need to be totally rethought once
          // multiple images are possible on one product.
          // This currently does a removal of any existing image any
          // time a new one is added. Consider it a hack.
          vm.handleRemoveImage()

          vm.applyImageToCanvas(fabricImage, exifData)
        }

        vm.loading = false
      }

      fabric.Image.fromURL(imageUrl, handleImageCallback, { crossOrigin: 'anonymous' })
    },

    applyImageToCanvas (fabricImage, exifData) {
      let vm = this

      let {exifOrientation, exifNumber} = exifData
      let scaleFactor = getScaleFactor(fabricImage, exifNumber, vm.canvas)

      vm.initialUserImageScale = scaleFactor * 1.0

      fabricImage.id = `img${makeid(6)}`
      fabricImage.originX = 'center'
      fabricImage.originY = 'center'
      fabricImage.scaleX = scaleFactor
      fabricImage.scaleY = scaleFactor
      fabricImage.left = vm.artboard_width / 2
      fabricImage.top = vm.artboard_height / 2
      fabricImage.hasControls = false
      fabricImage.hasBorders = false

      vm.imageFileList.push({
        exifNumber,
        exifOrientation,
        id: fabricImage.id,
        scaleFactor: scaleFactor
      })

      var selectedFrame = vm.selectedTemplate.filter(mask => {
        if (vm.draggedInOrViaDialog === 'draggedIn') {
          if (mask.id === vm.activeMask) {
            mask['img'] = fabricImage.id
          }
          return mask.id === vm.activeMask
        } else if (vm.draggedInOrViaDialog === 'viaDialog') {
          if (mask.id === vm.selectedMask) {
            mask['img'] = fabricImage.id
          }
          return mask.id === vm.selectedMask
        }
      })

      vm.visibleMasks.forEach(visibleMask => {
        if (visibleMask.id === selectedFrame[0].id) {
          visibleMask.img = fabricImage.id
        }
      })

      if (selectedFrame.length > 0) {
        let clipMask = new fabric[selectedFrame[0].type](selectedFrame[0].maskObj)
        fabricImage.clipPath = clipMask
      }

      vm.bindImageEvents(fabricImage)

      // ROTATE TO COMPENSATE FOR EXIF ORIENTATION:
      fabricImage.angle = exifOrientation || 0

      fabricImage.filters.push(new fabric.Image.filters.Brightness({ brightness: 0 }))
      fabricImage.filters.push(new fabric.Image.filters.Contrast({ contrast: 0 }))

      // Applying filters is supposed to fire up the underlying WebGL
      // rendering stuff. But in this case it does not.
      //
      // It's happening lazily, later on, when the user first slides a slider
      // or activates a filter. It would be nicer to suffer that pain initially
      // during the loading of the image, so that once the image is loaded the
      // user is free to do whatever they want without delay/latency.
      //
      // fabric.filterBackend = fabric.initFilterBackend();

      vm.canvas.setActiveObject(fabricImage)
      vm.canvas.controlsAboveOverlay = true
      vm.canvas.add(fabricImage)
      vm.canvas.sendToBack(fabricImage)
      vm.activeControl = 'scaleRotate'
      vm.canvas.renderAll()
    },

    setTemplateImages (callback) {
      // ASSUMES ONLY TWO TEMPLATE IMAGES (base & detail overlay)
      // AND TAKES A CALLBACK:
      var vm = this

      var renderAndCb = function (canvas) {
        vm.canvas.renderAll(canvas)
        callback()
      }

      var setOverlay = function (canvas) {
        vm.canvas.setOverlayImage(`${vm.template.mask_details}`, renderAndCb.bind(vm.canvas), {
          left: vm.artboard_width / 2,
          top: vm.artboard_height / 2,
          scaleX: vm.globalScaleFactor,
          scaleY: vm.globalScaleFactor,
          originX: 'center',
          originY: 'center',
          crossOrigin: 'anonymous'
        })
      }

      vm.canvas.setBackgroundImage(`${vm.template.base}`, setOverlay.bind(vm.canvas), {
        left: vm.artboard_width / 2,
        top: vm.artboard_height / 2,
        scaleX: vm.globalScaleFactor,
        scaleY: vm.globalScaleFactor,
        originX: 'center',
        originY: 'center',
        crossOrigin: 'anonymous'
      })
    },

    setTemplateStack (templateName) {
      /*
        DRAWING TEMPLATE MASKS:
        After template/product images have loaded,
        we copy the chosen template from
        its computed property, and draw a cooresponding
        set of visual indicators onto the canvas to
        represent it's customizable regions.
      */
      this.selectedTemplateName = templateName

      let onComplete = () => {
        // DRAW CLIP PATHS FOR GIVEN TEMPLATE:
        this.selectedTemplate.forEach(maskObj => {
          let maskCopy = _.clone(maskObj)

          let constructor = fabric[maskCopy.type]
          let visibleMask = new constructor(maskCopy.maskObj)

          maskCopy.maskObj = visibleMask

          visibleMask.set({
            fill: 'rgba(0, 0, 0, 0)',
            hasControls: false,
            hasBorders: false,
            selectable: false,
            evented: false
          })

          this.loading = false

          unUi.$emit(REVEAL_INTRO_EVENT)

          // TODO MASKS?
          // this.canvas.add(visibleMask).renderAll()

          this.visibleMasks.push(maskCopy)
        })

        // TODO rename this ... it's confusing
        this.activateCustomizer()
      }

      this.setTemplateImages(onComplete)
    },

    handleOffClick (canvasEl) {
      canvasEl.addEventListener('click', (e) => {
        if (e.target !== canvasEl) {
          return
        }

        this.imageIsSelected = false
        this.canvas.discardActiveObject().renderAll()
        this.activeControl = null
        unUi.$emit('deselectAllDesignControls')
        this.deselectAllMasks()
      })
    },

    initFabric () {
      if (this.canvas) {
        this.canvas.clear()
        this.canvas.dispose()
      }

      var vm = this

      // Fabric demands a high value here to support big images
      fabric.textureSize = 2048 * 2
      fabric.filterBackend = fabric.initFilterBackend()

      var canvasWrap = document.getElementById('un_product_art_group')
      var maxWidth = canvasWrap.offsetWidth > 600 ? 600 : canvasWrap.offsetWidth
      var maxHeight = canvasWrap.offsetHeight
      var baseImageWidth
      var baseImageHeight

      var baseImage = new Image()
      baseImage.crossOrigin = 'Anonymous'

      baseImage.onload = (e) => {
        baseImageWidth = baseImage.width
        baseImageHeight = baseImage.height

        // this.handleOffClick(canvasWrap)

        // FROM THE DESIRED PRINT OUTPUT'S DIMENSIONS,
        // DETERMINE A SCALE RATIO TO MAKE THE CANVAS'S ASPECT RATIO
        // THE SAME AS THE DESIRED OUTPUT'S RATIO BUT SET TO FIT INSIDE OUR WEB VIEW:
        var scaleRatio

        if (this.template.production_size.w > this.template.production_size.h) {
          // scaleRatio = maxWidth / this.template.production_size.w
          scaleRatio = maxWidth / baseImageWidth
        } else if (this.template.production_size.w <= this.template.production_size.h) {
          // scaleRatio = maxHeight / this.template.production_size.h
          scaleRatio = maxHeight / baseImageHeight
        }

        this.globalScaleFactor = scaleRatio

        var newWidth = baseImageWidth * scaleRatio
        var newHeight = baseImageHeight * scaleRatio

        this.artboard_width = newWidth
        this.artboard_height = newHeight

        this.canvas = new fabric.Canvas('un_product_art_board', {
          preserveObjectStacking: true,
          width: vm.artboard_width,
          height: vm.artboard_height,
          hasControls: false,
          hasBorders: false,
          hasRotatingPoint: false
        })

        this.setTemplateStack('edgeToEdge')
      }

      baseImage.src = this.template.base
    },

    getVariantTemplateViaAPI () {
      // Used w/ Gold
      const apiEndpoints = window.gold_endpoints

      let variant = null
      let template = null

      return new Promise((resolve, reject) => {
        if (!apiEndpoints) {
          reject(new Error('No API endpoints could be found.'))
          return
        }

        axiosBase.get(ENDPOINT_VARIANTS + apiEndpoints.variantId)
          .catch(reject)
          .then(variantResponse => {
            variant = variantResponse.data

            let width = variant.production_width
            let height = variant.production_height
            let templateId = variant.product.decoration.id

            let endpointStr = ENDPOINT_DECORATION_ASSET +
                              BY_TEMPLATE_ID +
                              templateId + '&' +
                              BY_W +
                              width + '&' +
                              BY_H +
                              height

            axiosBase.get(endpointStr)
              .catch(reject)
              .then(decorationAssetResponse => {
                if (!decorationAssetResponse.data.results.length || !variant.outline_image || !variant.base_image) {
                  reject(new Error('Insufficient data.'))
                } else {
                  template = {
                    artwork: decorationAssetResponse.data.results[0].artwork_overlay_image,
                    mask_details: variant.outline_image,
                    base: variant.base_image,
                    production_size: {
                      w: width,
                      h: height
                    }
                  }

                  resolve(template, variant)
                }
              })
          })
      })
    },

    getVariantTemplateViaWindow () {
      return new Promise((resolve, reject) => {
        let template = null

        // Used w/ Shopify
        const foundVariant = window.variants[window.currentGlobalSku]

        if (!foundVariant) {
          reject(new Error('No variant could be found on the window.'))
        }

        template = {
          artwork: foundVariant.artwork_overlay_image,
          mask_details: foundVariant.outline_image,
          base: foundVariant.base_image,
          production_size: {
            w: foundVariant.production_width,
            h: foundVariant.production_height
          }
        }

        resolve(template, foundVariant)
      })
    },

    loadTemplateStackFromVariant () {
      // This procedure kicks off the entire customizer.
      // - First, we choose the right loader based on our environment.
      // - These are promises with similar interfaces, so they intend to be interchangeable.
      // - The loader returns the (template, variant) as a tuple
      // - Then we set the `template` state attribute.
      // - A watcher waits for this template to change and acts accordingly.
      const loader = (this.isGoldEnv)
        ? this.getVariantTemplateViaAPI
        : this.getVariantTemplateViaWindow

      loader()
        .catch(console.error)
        .then((template, variant) => {
          this.template = template
        })
    }

    /****************************************************************

    At the moment this logic is not being used, as all of the current
    variants lack the 'artwork' (specifically: artwork_overlay_image) attribute
    This is effectively a no-op, so we'll comment it out and revisit later.

    addTemplateArtwork (callback) {
      // LOAD UP DECORATION ASSET:
      var vm = this

      if (!this.template.artwork) {
        callback()
      } else {
        fabric.util.loadImage(this.template.artwork, (img) => {
          var oImg = new fabric.Image(img)

          oImg.id = `templateArtwork`
          oImg.originX = 'center'
          oImg.originY = 'center'
          oImg.scaleX = vm.globalScaleFactor
          oImg.scaleY = vm.globalScaleFactor
          oImg.left = vm.artboard_width / 2
          oImg.top = vm.artboard_height / 2

          this.canvas.add(oImg)
          this.canvas.renderAll()

          oImg.dispose()

          callback()
        }, null, {crossOrigin: 'anonymous'})
      }
    },
    ****************************************************************/

  },

  mounted () {
    this.activateCustomizer()

    this.loadTemplateStackFromVariant()

    unUi.$on(RENDER_START_EVENT, () => {
      this.rendering = true
    })

    unUi.$on(RENDER_COMPLETE_EVENT, () => {
      this.rendering = false
    })

    unUi.$on(ALERT_USER_EVENT, (userAlert) => {
      this.alertUser(userAlert)
    })
  },

  beforeDestroy () {
    if (!this.canvas) { return }
    this.canvas.clear()
    this.canvas.dispose()
  },

  components: {
    ArtCanvas,
    DesignControls,
    Check,
    SpinningLoader,
    FileUploadProgress,
    UserAlert
  }
}
</script>

<style lang='scss'>
#un_productdesign_container {
  @include full_wh;

  .back-btn {
    font-size: 1rem;
    text-transform: uppercase;
    color: #9c9aab;
    position: absolute;
    top: 2rem;
    left: 2rem;

    @include type_medium;

    letter-spacing: 0.02rem;
    z-index: 999;
    cursor: pointer;
    border: 1px solid #e6e5ea;
    padding: 0.35rem 0.75rem;
    border-radius: 1.35rem;
  }

  &.env-gold {
    left: 18rem;
    width: calc(100% - 18rem);
  }

  .spinning_loader {
    position: absolute;
    display: block;
    left: 50%;
    top: calc(50% - 2rem);
    transform: translateX(-50%) translateY(-50%);
    margin: 0;

    &:after {
      background: white;
    }
  }

  #un_productdesign_wrap {
    opacity: 0;
    visibility: hidden;
    transition: all 0.4s ease-in-out;

    &.active {
      opacity: 1;
      visibility: visible;
    }

    .artCanvasEl {
      transform: translateX(-50%) scale(1);
      opacity: 1;
      visibility: visible;
      transition: all 0.45s ease-in-out;

      &.deactivated, &.uploading {
        opacity: 0;
        visibility: hidden;
        transform: translateX(-50%) scale(0.6);
      }
    }

    .acceptance-controls_wrap {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 3.5rem;
      text-align: center;
      transition: all 0.45s ease-in-out;
      transform: translate3d(0px, 0px, 0px);

      .acceptance-options {
        button {
          background-color: transparent;
          border: 0px;
          outline: none;
          padding: 1em;
          cursor: pointer;
        }

        .btn-separator {
          background-color: #dcdcdc;
          width: 1px;
          display: inline-block;
          height: 1em;
          margin: .5em 0.5em 0 0.5em;
        }

        @include respond-to("small") {
          border-bottom: 1px solid #e2e2e2;
          padding: 0;

          .btn-separator {
            display: none;
          }

          button {
            padding: 0;
            margin: 0;
            width: 49%;
            height: 3.4rem;

            &.cancel-btn{
              border-right:1px solid #dcdcdc;
            }
          }
        }
      }

      p {
        @include font-size(1);

        text-decoration: underline;
      }

      &.deactivated {
        transform: translate3d(0px, -100px, 0px);
      }

      &.uploading {
        opacity: 0.2;
        pointer-events: none;
      }
    }
  }
}

.un_dragcta {
  position: relative;
}

#un_productdesign_controls {
  width: 65%;
  height: 20rem;
  left: 50%;
  transform: translateX(-50%) translateY(0px);
  bottom: 0;
  transition: all 0.45s ease-in-out;

  &.deactivated {
    transform: translateX(-50%) translateY(15rem);
  }

  &.uploading {
    opacity: 0.2;
    pointer-events: none;
  }

  @include respond-to("small") {
    width: 100%;
  }
}

.fileUploadEl {
  position: absolute;
  margin: 0 auto;
  width: 100%;
  text-align: center;
  top: 50%;
  transform: translate3d(0px, -50%, 0px);
  height: 18rem;
}

#un_user-alert-container {
  position: absolute;
  top: 4.5rem;
  width: 100%;
  z-index: 9999;
  background: rgba(#FFF, 0.8);

  @include respond-to("small") {
    width: calc(100% - 2rem);
    left: 1rem;
    right: 1rem;
  }
}

#un_completedesigncontrol_wrap {
  position: absolute;
  top: 0;
  right: 0;
  width: $s_completedesigncontrols_wrap_w;
  height: $s_completedesigncontrols_wrap_h;
}

#un_designcontrol_complete {
  width: $s_completedesigncontrols_btn;
  height: $s_completedesigncontrols_btn;
  border-radius: 100%;
  color: $c_pure_blue;
  border: none;

  @include forceYCenter;

  outline: none;
  overflow: hidden;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: $c_designcontrols_icon_bg;
    opacity: 0.7;

    @include t_btn_hover;
  }

  .icon {
    position: relative;
    top: 0.1rem;
    margin: 0 auto;
    width: 1.3rem;
    height: auto;
    z-index: 2;
  }

  &:hover {
    &:after {
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// uncommon-client/components/product-designer/DesignProduct.vue